import React from 'react';

// Selector de hora en formato 12h (AM/PM).
// `<input type="time">` muestra 12h o 24h según el idioma del navegador y no
// se puede forzar, así que aquí va un control propio. Lee y escribe siempre
// "HH:mm" en 24h, que es como se guarda en la base de datos.
const pad = (n) => String(n).padStart(2, '0');

const parse24 = (value) => {
    const [rawH, rawM] = String(value || '').split(':');
    const h = parseInt(rawH, 10);
    const m = parseInt(rawM, 10);
    if (isNaN(h) || isNaN(m)) return { h12: 9, minute: 0, period: 'AM' };
    return {
        h12: h % 12 === 0 ? 12 : h % 12,
        minute: m,
        period: h >= 12 ? 'PM' : 'AM',
    };
};

const to24 = (h12, minute, period) => {
    let h = h12 % 12;
    if (period === 'PM') h += 12;
    return `${pad(h)}:${pad(minute)}`;
};

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5);

// `allowEmpty` deja el control en blanco hasta que se elige algo, para
// formularios donde "sin hora" es un valor válido (ej. salida pendiente).
const Time12Input = ({ value, onChange, disabled, allowEmpty, className = '' }) => {
    const empty = allowEmpty && !value;
    const { h12, minute, period } = parse24(value);

    // Si los minutos guardados no caen en el paso de 5 (ej. 17:32), se añaden
    // a la lista para no perder el valor original al abrir el selector.
    const minuteOptions = MINUTES.includes(minute) ? MINUTES : [...MINUTES, minute].sort((a, b) => a - b);

    const emit = (nh, nm, np) => onChange(to24(nh, nm, np));
    // Al elegir la primera parte estando vacío se asume el resto por defecto,
    // que es lo que espera quien escribe "5" y luego "30 PM".
    const emitFromEmpty = (patch) => onChange(to24(patch.h ?? 9, patch.m ?? 0, patch.p ?? 'AM'));

    const selectClass = 'px-1.5 py-1 border border-gray-200 rounded text-xs font-mono bg-white focus:ring-1 focus:ring-blue-400 outline-none disabled:bg-gray-50 disabled:text-gray-400 cursor-pointer';

    return (
        <span className={`inline-flex items-center gap-0.5 ${className}`}>
            <select disabled={disabled} className={selectClass} value={empty ? '' : h12}
                onChange={(e) => {
                    if (e.target.value === '') return onChange('');
                    const h = Number(e.target.value);
                    return empty ? emitFromEmpty({ h }) : emit(h, minute, period);
                }}>
                {allowEmpty && <option value="">--</option>}
                {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
            </select>
            <span className="text-gray-400 text-xs font-bold">:</span>
            <select disabled={disabled} className={selectClass} value={empty ? '' : minute}
                onChange={(e) => {
                    if (e.target.value === '') return onChange('');
                    const m = Number(e.target.value);
                    return empty ? emitFromEmpty({ m }) : emit(h12, m, period);
                }}>
                {allowEmpty && <option value="">--</option>}
                {minuteOptions.map(m => <option key={m} value={m}>{pad(m)}</option>)}
            </select>
            <select disabled={disabled} className={`${selectClass} ml-0.5 font-semibold`} value={empty ? '' : period}
                onChange={(e) => {
                    if (e.target.value === '') return onChange('');
                    return empty ? emitFromEmpty({ p: e.target.value }) : emit(h12, minute, e.target.value);
                }}>
                {allowEmpty && <option value="">--</option>}
                <option value="AM">AM</option>
                <option value="PM">PM</option>
            </select>
            {allowEmpty && !empty && !disabled && (
                <button type="button" onClick={() => onChange('')}
                    className="ml-0.5 px-1 text-gray-300 hover:text-red-500 text-xs font-bold" title="Limpiar">×</button>
            )}
        </span>
    );
};

export default Time12Input;
